import type { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import i18n from '@/i18n';
import { errorNotificationIcon, warningNotificationIcon } from '@/theme/notificationIcons';
import { isNetworkError, isTimeoutError } from '@/utils/apiError';
import { sendClientLog } from '@/utils/clientLog';

// ── Helpers ───────────────────────────────────────────────────────

const isAuthEndpoint = (error: unknown): boolean => {
  const url = (error as AxiosError)?.config?.url ?? '';
  return url.includes('/auth/');
};

const httpStatus = (error: unknown): number | undefined => (error as AxiosError)?.response?.status;

// ── Globalni error handler ────────────────────────────────────────

const handleError = (error: unknown) => {
  console.error('[API Error]', error);
  sendClientLog({
    level: 'error',
    context: 'API',
    message: axios.isAxiosError(error)
      ? `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${error.response?.status}`
      : String(error)
  });

  // Auth endpointi upravljaju greškama sami (useAuth, LoginForm)
  if (isAuthEndpoint(error)) return;

  // 401 — interceptor u client.ts već redirecta na /login
  if (httpStatus(error) === 401) return;

  const t = i18n.t.bind(i18n);

  if (isTimeoutError(error)) {
    notifications.show({
      color: 'orange',
      icon: warningNotificationIcon,
      title: t('errors.timeout.title'),
      message: t('errors.timeout.message'),
      autoClose: 8000
    });
    return;
  }

  if (isNetworkError(error)) {
    notifications.show({
      color: 'red',
      icon: errorNotificationIcon,
      title: t('errors.network.title'),
      message: t('errors.network.message'),
      autoClose: 8000
    });
    return;
  }

  const status = httpStatus(error);

  if (status === 403) {
    notifications.show({
      color: 'red',
      icon: errorNotificationIcon,
      title: t('errors.forbidden.title'),
      message: t('errors.forbidden.message'),
      autoClose: 6000
    });
    return;
  }

  if (status && status >= 500) {
    notifications.show({
      color: 'red',
      icon: errorNotificationIcon,
      title: t('errors.server.title'),
      message: t('errors.server.message'),
      autoClose: 8000
    });
    return;
  }

  notifications.show({
    color: 'red',
    icon: errorNotificationIcon,
    title: t('errors.unknown.title'),
    message: t('errors.unknown.message'),
    autoClose: 6000
  });
};

// ── QueryClient ───────────────────────────────────────────────────

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleError
  }),
  mutationCache: new MutationCache({
    // Mutacije koje već imaju vlastiti prikaz greške (lokalni Toast/notifications.show/
    // inline tekst, izveden iz iste error poruke) postave `meta: { skipGlobalErrorToast: true }`
    // da se izbjegne dupli prikaz — vidi "Isti obrazac kao..." komentare uz te mutacije.
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.skipGlobalErrorToast) return;
      handleError(error);
    }
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 0
    }
  }
});
